import React from 'react';
import { COLORS } from '@/consts/colors';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import styles from './ColorPicker.module.css';
import { ChromePicker, SketchPicker } from 'react-color';
import { ThemeOverride } from '@/providers/ThemeOverride';
import { TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Tooltip } from './ui/tooltip';

export const ColorPicker = React.forwardRef<
  HTMLInputElement,
  {
    value?: string | null;
    onChange: (value: string) => void;
    disabled?: boolean;
    name?: string;
    className?: string;
    picker?: 'simple' | 'chrome' | 'sketch';
    trigger?: 'circle' | 'square' | React.ReactNode;
    showAlpha?: boolean;
    tooltip?: string;
  }
>(
  (
    {
      value,
      onChange,
      disabled,
      name,
      className,
      picker = 'simple',
      trigger = 'circle',
      showAlpha = false,
      tooltip,
    },
    ref,
  ) => {
    value = value ?? '';
    return (
      <div className={className}>
        <input type="hidden" name={name} value={value} ref={ref} />
        <Popover>
          <PopoverTrigger disabled={disabled} asChild>
            {typeof trigger === 'string' &&
            ['circle', 'square'].includes(trigger) ? (
              <ColorTrigger
                trigger={trigger as 'circle' | 'square'}
                value={value}
                tooltip={tooltip}
              />
            ) : (
              trigger
            )}
          </PopoverTrigger>
          <PopoverContent className="w-auto rounded-none border-none bg-transparent p-0 shadow-none">
            {picker === 'simple' && (
              <div className="flex w-72 flex-row flex-wrap items-start justify-start rounded bg-muted p-2">
                {COLORS.map((c) => (
                  <ColorTrigger
                    key={c}
                    className={c === value ? styles.selected : ''}
                    value={c}
                    onClick={() => onChange(c)}
                  />
                ))}
              </div>
            )}
            {picker === 'chrome' && (
              <ThemeOverride theme="light">
                <ChromePicker
                  color={value}
                  onChangeComplete={(color) => onChange(color.hex)}
                  disableAlpha={!showAlpha}
                />
              </ThemeOverride>
            )}
            {picker === 'sketch' && (
              <ThemeOverride theme="light">
                <SketchPicker
                  color={value}
                  onChangeComplete={(color) => onChange(color.hex)}
                />
              </ThemeOverride>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  },
);

ColorPicker.displayName = 'ColorPicker';
const ColorTrigger = React.forwardRef<
  HTMLDivElement,
  {
    trigger?: 'circle' | 'square';
    value?: string;
    onClick?: () => void;
    className?: string;
    style?: React.CSSProperties;
    tooltip?: string;
  }
>(({ trigger, value, onClick, className, style, tooltip }, ref) => {
  const triggerElement = (
    <div
      ref={ref}
      onClick={onClick}
      style={{ backgroundColor: value || 'transparent', ...style }}
      className={` ${trigger === 'square' ? styles.colorSquare : styles.colorCircle} ${className || ''} ${onClick ? 'cursor-pointer' : ''} `}
    />
  );

  if (tooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{triggerElement}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    );
  }
  return triggerElement;
});

ColorTrigger.displayName = 'ColorCircle';
